import { convexQuery } from "@convex-dev/react-query";
import { useQuery } from "@tanstack/react-query";
import type { Id } from "@teachloop/backend/_generated/dataModel";
import { useLocalSearchParams, useRouter } from "expo-router";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Pressable, ScrollView, Text, View } from "react-native";
import DownArrowIcon from "@/assets/svgs/down-arrow-icon";
import ExamBoardMarkerIcon from "@/assets/svgs/exam-board-marker-icon";
import ExperienceIcon from "@/assets/svgs/experience-icon";
import ResponsiveContent from "@/elements/components/shared/responsive-content";
import { useSheet } from "@/elements/components/shared/sheet-provider";
import type {
  ChildrenSelectSheetPayload,
  ChildrenSelectSheetResult,
} from "@/elements/components/sheets/children-select-sheet";
import type {
  MultiSelectSheetPayload,
  MultiSelectSheetResult,
} from "@/elements/components/sheets/multi-select-sheet";
import type {
  SingleSelectSheetPayload,
  SingleSelectSheetResult,
} from "@/elements/components/sheets/single-select-sheet";
import { Avatar } from "@/elements/components/ui/avatar";
import { Input } from "@/elements/components/ui/input";
import { Tag } from "@/elements/components/ui/tag";
import { api } from "@/elements/components/utils/convex-api";
import { SheetIds } from "@/elements/components/utils/sheet-ids";
import { isIbCurriculumLabel } from "@/elements/components/utils/curriculum";

type SearchMode = "child" | "advanced";
type SearchLevel = "ks2" | "ks3" | "ks4" | "a_level";

function parseSubjectIds(value: string | string[] | undefined): string[] {
  if (!value) return [];
  const raw = Array.isArray(value) ? value.join(",") : value;
  return raw
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean);
}

function FindTutorScreen() {
  const router = useRouter();
  const params = useLocalSearchParams<{
    mode?: string;
    childId?: string;
    subjectIds?: string;
    level?: string;
    curriculumId?: string;
    programmeId?: string;
    q?: string;
  }>();

  const mode: SearchMode = params.mode === "advanced" ? "advanced" : "child";
  const childIdParam = typeof params.childId === "string" ? params.childId : undefined;
  const levelParam =
    typeof params.level === "string" && params.level.length > 0
      ? (params.level as SearchLevel)
      : undefined;
  const curriculumIdParam =
    typeof params.curriculumId === "string" && params.curriculumId.length > 0
      ? params.curriculumId
      : undefined;
  const programmeIdParam =
    typeof params.programmeId === "string" && params.programmeId.length > 0
      ? params.programmeId
      : undefined;
  const nameQueryParam = typeof params.q === "string" ? params.q : "";
  const subjectIdsFromParams = useMemo(
    () => parseSubjectIds(params.subjectIds),
    [params.subjectIds],
  );

  const [nameInput, setNameInput] = useState(nameQueryParam);
  const nameDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const updateParams = useCallback(
    (next: {
      mode?: SearchMode;
      childId?: string | null;
      subjectIds?: string[] | null;
      level?: SearchLevel | null;
      curriculumId?: string | null;
      programmeId?: string | null;
      q?: string | null;
    }) => {
      const nextMode = next.mode ?? mode;
      const nextChildId =
        next.childId === undefined ? childIdParam : next.childId || undefined;
      const nextSubjectIds =
        next.subjectIds === undefined
          ? subjectIdsFromParams
          : next.subjectIds ?? [];
      const nextLevel =
        next.level === undefined ? levelParam : next.level || undefined;
      const nextCurriculumId =
        next.curriculumId === undefined
          ? curriculumIdParam
          : next.curriculumId || undefined;
      const nextProgrammeId =
        next.programmeId === undefined
          ? programmeIdParam
          : next.programmeId || undefined;
      const nextQ =
        next.q === undefined ? nameQueryParam : next.q || undefined;

      router.setParams({
        mode: nextMode,
        childId: nextChildId ?? "",
        subjectIds: nextSubjectIds.join(","),
        level: nextLevel ?? "",
        curriculumId: nextCurriculumId ?? "",
        programmeId: nextProgrammeId ?? "",
        q: nextQ ?? "",
      });
    },
    [
      router,
      mode,
      childIdParam,
      subjectIdsFromParams,
      levelParam,
      curriculumIdParam,
      programmeIdParam,
      nameQueryParam,
    ],
  );

  const { data: children = [] } = useQuery(
    convexQuery(api.parent.findTutors.queries.children, {}),
  );
  const { data: filterOptions } = useQuery(
    convexQuery(api.parent.findTutors.queries.getSearchFilterOptions, {}),
  );

  const [firstChild] = children;
  const firstChildId = firstChild?._id;
  const hasMultipleChildren = children.length > 1;
  const selectedChildId = childIdParam ?? firstChildId ?? null;
  const selectedChild = children.find((child) => child._id === selectedChildId);
  const selectedSubjectIds = subjectIdsFromParams;

  const { open: openChildrenSheet } = useSheet<
    ChildrenSelectSheetPayload,
    ChildrenSelectSheetResult
  >(SheetIds.CHILDREN_SELECT);
  const { open: openSubjectsSheet } = useSheet<
    MultiSelectSheetPayload,
    MultiSelectSheetResult
  >(SheetIds.MULTI_SELECT);
  const { open: openLevelSheet } = useSheet<
    SingleSelectSheetPayload,
    SingleSelectSheetResult
  >(SheetIds.SINGLE_SELECT);
  const { open: openCurriculumSheet } = useSheet<
    SingleSelectSheetPayload,
    SingleSelectSheetResult
  >(SheetIds.SINGLE_SELECT);
  const { open: openProgrammeSheet } = useSheet<
    SingleSelectSheetPayload,
    SingleSelectSheetResult
  >(SheetIds.SINGLE_SELECT);

  const { data: studentSubjects = [] } = useQuery(
    convexQuery(
      api.parent.findTutors.queries.studentSubjects,
      mode === "child" && selectedChild?._id
        ? { studentId: selectedChild._id as Id<"students"> }
        : "skip",
    ),
  );

  const allSubjects = filterOptions?.subjects ?? [];
  const levels = filterOptions?.levels ?? [];
  const curricula = filterOptions?.curricula ?? [];
  const selectedLevelLabel = levels.find(
    (level) => level.value === levelParam,
  )?.label;
  const selectedCurriculum = curricula.find(
    (curriculum) => curriculum.value === curriculumIdParam,
  );
  const isIbCurriculumSelected = isIbCurriculumLabel(
    selectedCurriculum?.label,
  );

  const { data: programmes = [] } = useQuery(
    convexQuery(
      api.parent.findTutors.queries.getProgrammesForCurriculum,
      isIbCurriculumSelected && curriculumIdParam
        ? { curriculumId: curriculumIdParam as Id<"curriculum"> }
        : "skip",
    ),
  );
  const selectedProgramme = programmes.find(
    (programme) => programme.value === programmeIdParam,
  );

  const { data: filteredAdvancedSubjects = [] } = useQuery(
    convexQuery(
      api.parent.findTutors.queries.getSubjectsForFilters,
      mode === "advanced"
        ? {
            level:
              !isIbCurriculumSelected && levelParam ? levelParam : undefined,
            programmeId:
              isIbCurriculumSelected && programmeIdParam
                ? (programmeIdParam as Id<"keyStages">)
                : undefined,
          }
        : "skip",
    ),
  );

  const advancedSubjects =
    mode === "advanced" ? filteredAdvancedSubjects : allSubjects;
  const subjectOptions = mode === "child" ? studentSubjects : advancedSubjects;
  const selectedSubjects = subjectOptions.filter((subject) =>
    selectedSubjectIds.includes(subject.value),
  );

  const childDidInitSubjects = useRef(false);

  useEffect(() => {
    if (mode !== "child") return;
    if (childIdParam || !firstChildId) return;
    updateParams({ childId: firstChildId });
  }, [mode, childIdParam, firstChildId, updateParams]);

  useEffect(() => {
    if (mode !== "child") {
      childDidInitSubjects.current = false;
      return;
    }
    if (childDidInitSubjects.current) return;
    if (subjectIdsFromParams.length > 0) {
      childDidInitSubjects.current = true;
      return;
    }
    if (studentSubjects.length === 0) return;
    childDidInitSubjects.current = true;
    updateParams({
      subjectIds: studentSubjects.map((subject) => subject.value),
    });
  }, [mode, subjectIdsFromParams.length, studentSubjects, updateParams]);

  useEffect(() => {
    setNameInput(nameQueryParam);
  }, [nameQueryParam]);

  useEffect(() => {
    if (mode !== "advanced") return;
    if (selectedSubjectIds.length === 0) return;
    if (filteredAdvancedSubjects.length === 0) return;

    const allowedIds = new Set(
      filteredAdvancedSubjects.map((subject) => subject.value),
    );
    const nextSubjectIds = selectedSubjectIds.filter((id) =>
      allowedIds.has(id as Id<"subjects">),
    );
    if (nextSubjectIds.length === selectedSubjectIds.length) return;
    updateParams({ subjectIds: nextSubjectIds });
  }, [
    mode,
    filteredAdvancedSubjects,
    selectedSubjectIds,
    updateParams,
  ]);

  useEffect(() => {
    return () => {
      if (nameDebounceRef.current) clearTimeout(nameDebounceRef.current);
    };
  }, []);

  const { data: childTutors = [] } = useQuery(
    convexQuery(
      api.parent.findTutors.queries.findTutors,
      mode === "child" &&
        selectedSubjectIds.length > 0 &&
        selectedChild?.yearGroupId &&
        selectedChild?._id
        ? {
            subjectIds: selectedSubjectIds as Id<"subjects">[],
            yearGroupId: selectedChild.yearGroupId as Id<"yearGroups">,
            studentId: selectedChild._id as Id<"students">,
          }
        : "skip",
    ),
  );

  const { data: advancedTutors = [] } = useQuery(
    convexQuery(
      api.parent.findTutors.queries.searchTutors,
      mode === "advanced"
        ? {
            nameQuery: nameQueryParam || undefined,
            subjectIds:
              selectedSubjectIds.length > 0
                ? (selectedSubjectIds as Id<"subjects">[])
                : undefined,
            level: isIbCurriculumSelected ? undefined : levelParam,
            curriculumId: curriculumIdParam
              ? (curriculumIdParam as Id<"curriculum">)
              : undefined,
            programmeId:
              isIbCurriculumSelected && programmeIdParam
                ? (programmeIdParam as Id<"keyStages">)
                : undefined,
          }
        : "skip",
    ),
  );

  const tutors = mode === "child" ? childTutors : advancedTutors;

  function handleNameChange(text: string) {
    setNameInput(text);
    if (nameDebounceRef.current) clearTimeout(nameDebounceRef.current);
    nameDebounceRef.current = setTimeout(() => {
      updateParams({ q: text.trim() || null });
    }, 300);
  }

  return (
    <View className="flex-1 bg-teachLoop-creamBase">
      <ScrollView showsVerticalScrollIndicator={false} keyboardShouldPersistTaps="handled">
        <ResponsiveContent>
          <View className="h-3" />
          <View className="mx-5">
            <Text className="font-zilla-slab-medium font-medium text-black text-[32px] leading-[1.2]">
              Find a tutor
            </Text>

            <View className="h-6" />

            <View className="flex-row gap-2">
              <Pressable
                onPress={() =>
                  updateParams({
                    mode: "child",
                    level: null,
                    curriculumId: null,
                    programmeId: null,
                    q: null,
                  })
                }
                className={`flex-1 rounded-xl border px-3 py-2 ${
                  mode === "child"
                    ? "bg-teachLoop-creamDark border-teachLoop-creamDark"
                    : "border-teachLoop-creamDark bg-transparent"
                }`}
              >
                <Text
                  className={`text-center font-geologica-medium text-sm text-gray-800 ${
                    mode === "child" ? "font-medium" : "font-normal"
                  }`}
                >
                  For my child
                </Text>
              </Pressable>
              <Pressable
                onPress={() =>
                  updateParams({
                    mode: "advanced",
                    subjectIds: [],
                    childId: childIdParam ?? null,
                  })
                }
                className={`flex-1 rounded-xl border px-3 py-2 ${
                  mode === "advanced"
                    ? "bg-teachLoop-creamDark border-teachLoop-creamDark"
                    : "border-teachLoop-creamDark bg-transparent"
                }`}
              >
                <Text
                  className={`text-center font-geologica-medium text-sm text-gray-800 ${
                    mode === "advanced" ? "font-medium" : "font-normal"
                  }`}
                >
                  Advanced search
                </Text>
              </Pressable>
            </View>

            <View className="h-6" />

            {mode === "child" ? (
              <View className="gap-4">
                <View className="flex-row items-center gap-2">
                  <Text className="font-geologica-medium text-black text-base leading-[1.5]">
                    Showing tutors for:
                  </Text>
                  <View className="flex-row items-center">
                    {hasMultipleChildren ? (
                      <Pressable
                        disabled={!selectedChildId}
                        className="flex-row items-center gap-2 bg-teachLoop-creamLight border border-gray-200 rounded-[15px] p-2"
                        onPress={async () => {
                          if (!selectedChildId) return;
                          const result = await openChildrenSheet({
                            title: "Show tutors for",
                            options: children.map((child) => ({
                              label: child.name,
                              value: child._id,
                              imageUrl: child.imageUrl,
                            })),
                            preSelectedOption: selectedChildId,
                          });
                          if (result.selectedOption) {
                            childDidInitSubjects.current = false;
                            updateParams({
                              childId: result.selectedOption as string,
                              subjectIds: [],
                            });
                          }
                        }}
                      >
                        <Avatar size="sm">
                          <Avatar.Content
                            size="sm"
                            labelSize="sm"
                            label={selectedChild?.name ?? ""}
                            source={
                              selectedChild?.imageUrl
                                ? { uri: selectedChild.imageUrl }
                                : undefined
                            }
                          />
                        </Avatar>
                        <Text className="font-geologica-medium text-gray-800 text-base leading-[1.5]">
                          {selectedChild?.name}
                        </Text>
                        <DownArrowIcon pathClassName="fill-teachLoop-textPrimary" />
                      </Pressable>
                    ) : (
                      <View className="flex-row items-center gap-2">
                        <Avatar size="sm">
                          <Avatar.Content
                            size="sm"
                            labelSize="sm"
                            label={firstChild?.name ?? ""}
                            source={
                              firstChild?.imageUrl
                                ? { uri: firstChild.imageUrl }
                                : undefined
                            }
                          />
                        </Avatar>
                        <Text className="font-geologica-medium text-gray-800 text-base leading-[1.5]">
                          {firstChild?.name ?? ""}
                        </Text>
                      </View>
                    )}
                  </View>
                </View>

                <View className="flex-row items-center gap-2 flex-wrap">
                  <Text className="font-geologica-medium font-medium text-black text-base leading-[1.5]">
                    Subjects:
                  </Text>
                  {selectedSubjects.map((subject) => (
                    <Tag size="md" close key={subject.value}>
                      <Tag.Text size="md">{subject.label}</Tag.Text>
                      <Tag.Close
                        onPress={() => {
                          updateParams({
                            subjectIds: selectedSubjectIds.filter(
                              (id) => id !== subject.value,
                            ),
                          });
                        }}
                      />
                    </Tag>
                  ))}

                  <Pressable
                    onPress={async () => {
                      const result = await openSubjectsSheet({
                        options: studentSubjects.map((subject) => ({
                          label: subject.label,
                          value: subject.value,
                        })),
                        preSelectedOptions: selectedSubjectIds,
                      });
                      if (result?.selectedOptions) {
                        updateParams({ subjectIds: result.selectedOptions });
                      }
                    }}
                  >
                    <DownArrowIcon pathClassName="fill-teachLoop-textPrimary" />
                  </Pressable>
                </View>
              </View>
            ) : (
              <View className="gap-4">
                <View className="gap-2">
                  <Text className="font-geologica-medium text-black text-base leading-[1.5]">
                    Name
                  </Text>
                  <Input
                    size="md"
                    placeholder="Search by tutor name"
                    value={nameInput}
                    onChangeText={handleNameChange}
                    autoCapitalize="words"
                    autoCorrect={false}
                  />
                </View>

                <View className="flex-row items-center gap-2 flex-wrap">
                  <Text className="font-geologica-medium font-medium text-black text-base leading-[1.5]">
                    Curriculum:
                  </Text>
                  {selectedCurriculum ? (
                    <Tag size="md" close>
                      <Tag.Text size="md">{selectedCurriculum.label}</Tag.Text>
                      <Tag.Close
                        onPress={() =>
                          updateParams({
                            curriculumId: null,
                            programmeId: null,
                          })
                        }
                      />
                    </Tag>
                  ) : (
                    <Text className="font-geologica-regular text-sm text-gray-500">
                      Any
                    </Text>
                  )}
                  <Pressable
                    onPress={async () => {
                      const result = await openCurriculumSheet({
                        title: "Select curriculum",
                        options: curricula.map((curriculum) => ({
                          label: curriculum.label,
                          value: curriculum.value,
                        })),
                        preSelectedOption: curriculumIdParam ?? "",
                      });
                      if (!result?.selectedOption) return;
                      const selectedCurriculumId = String(
                        result.selectedOption,
                      );
                      const nextCurriculum = curricula.find(
                        (curriculum) =>
                          curriculum.value === selectedCurriculumId,
                      );
                      const nextIsIb = isIbCurriculumLabel(
                        nextCurriculum?.label,
                      );
                      updateParams({
                        curriculumId: selectedCurriculumId,
                        level: nextIsIb ? null : undefined,
                        programmeId: null,
                      });
                    }}
                  >
                    <DownArrowIcon pathClassName="fill-teachLoop-textPrimary" />
                  </Pressable>
                </View>

                {isIbCurriculumSelected ? (
                  <View className="flex-row items-center gap-2 flex-wrap">
                    <Text className="font-geologica-medium font-medium text-black text-base leading-[1.5]">
                      Programme:
                    </Text>
                    {selectedProgramme ? (
                      <Tag size="md" close>
                        <Tag.Text size="md">{selectedProgramme.label}</Tag.Text>
                        <Tag.Close
                          onPress={() => updateParams({ programmeId: null })}
                        />
                      </Tag>
                    ) : (
                      <Text className="font-geologica-regular text-sm text-gray-500">
                        Any
                      </Text>
                    )}
                    <Pressable
                      onPress={async () => {
                        const result = await openProgrammeSheet({
                          title: "Select IB programme",
                          options: programmes.map((programme) => ({
                            label: programme.label,
                            value: programme.value,
                          })),
                          preSelectedOption: programmeIdParam ?? "",
                        });
                        if (!result?.selectedOption) return;
                        updateParams({
                          programmeId: String(result.selectedOption),
                        });
                      }}
                    >
                      <DownArrowIcon pathClassName="fill-teachLoop-textPrimary" />
                    </Pressable>
                  </View>
                ) : (
                  <View className="flex-row items-center gap-2 flex-wrap">
                    <Text className="font-geologica-medium font-medium text-black text-base leading-[1.5]">
                      Level:
                    </Text>
                    {levelParam ? (
                      <Tag size="md" close>
                        <Tag.Text size="md">
                          {selectedLevelLabel ?? levelParam}
                        </Tag.Text>
                        <Tag.Close
                          onPress={() => updateParams({ level: null })}
                        />
                      </Tag>
                    ) : (
                      <Text className="font-geologica-regular text-sm text-gray-500">
                        Any
                      </Text>
                    )}
                    <Pressable
                      onPress={async () => {
                        const result = await openLevelSheet({
                          title: "Select level",
                          options: levels.map((level) => ({
                            label: level.label,
                            value: level.value,
                          })),
                          preSelectedOption: levelParam ?? "",
                        });
                        if (result?.selectedOption) {
                          updateParams({
                            level: result.selectedOption as SearchLevel,
                          });
                        }
                      }}
                    >
                      <DownArrowIcon pathClassName="fill-teachLoop-textPrimary" />
                    </Pressable>
                  </View>
                )}

                <View className="flex-row items-center gap-2 flex-wrap">
                  <Text className="font-geologica-medium font-medium text-black text-base leading-[1.5]">
                    Subjects:
                  </Text>
                  {selectedSubjects.map((subject) => (
                    <Tag size="md" close key={subject.value}>
                      <Tag.Text size="md">{subject.label}</Tag.Text>
                      <Tag.Close
                        onPress={() => {
                          updateParams({
                            subjectIds: selectedSubjectIds.filter(
                              (id) => id !== subject.value,
                            ),
                          });
                        }}
                      />
                    </Tag>
                  ))}
                  <Pressable
                    onPress={async () => {
                      const result = await openSubjectsSheet({
                        options: advancedSubjects.map((subject) => ({
                          label: subject.label,
                          value: subject.value,
                        })),
                        preSelectedOptions: selectedSubjectIds,
                      });
                      if (result?.selectedOptions) {
                        updateParams({ subjectIds: result.selectedOptions });
                      }
                    }}
                  >
                    <DownArrowIcon pathClassName="fill-teachLoop-textPrimary" />
                  </Pressable>
                </View>
              </View>
            )}

            <View className="h-6" />

            {tutors.length === 0 ? (
              <View className="items-center py-10 gap-2">
                <Text className="font-geologica-medium text-base text-gray-800 text-center">
                  No tutors found
                </Text>
                <Text className="font-geologica-regular text-sm text-gray-600 text-center">
                  {mode === "child"
                    ? "Try selecting different subjects for this child."
                    : "Try adjusting name, curriculum, programme, subject, or level filters."}
                </Text>
              </View>
            ) : (
              <View className="gap-4 pb-8">
                {tutors.map((tutor) => (
                  <Pressable
                    key={tutor._id}
                    className="px-[17px] py-[25px] border border-teachLoop-creamDark rounded-[17px] gap-2"
                    onPress={() => {
                      const childQuery = selectedChildId
                        ? `?childId=${selectedChildId}`
                        : "";
                      router.push(
                        `/(app)/home/find-tutor/${tutor._id}${childQuery}`,
                      );
                    }}
                  >
                    <View className="flex-row items-start gap-5">
                      <View className="flex-column items-center gap-2">
                        <Avatar size="md">
                          <Avatar.Content
                            size="md"
                            labelSize="md"
                            label={tutor.name}
                            source={
                              tutor.imageUrl
                                ? { uri: tutor.imageUrl }
                                : undefined
                            }
                          />
                        </Avatar>
                        <Text className="font-geologica-medium font-medium text-black text-base leading-[1.5]">
                          {tutor.name}
                        </Text>
                      </View>
                      <View className="flex-1 gap-[11px]">
                        <Text
                          className="font-geologica-regular font-regular text-black text-sm leading-[1.5]"
                          numberOfLines={3}
                        >
                          {tutor.bio}
                        </Text>
                      </View>
                    </View>

                    <View className="flex-row items-center gap-2">
                      <ExperienceIcon
                        className="size-5"
                        pathClassName="stroke-black"
                      />
                      <Text
                        className="font-geologica-regular font-regular text-black text-sm leading-[1.5] flex-1"
                        numberOfLines={1}
                      >
                        {tutor.teachingExperience}
                      </Text>
                    </View>

                    {tutor.examBoardMarker && (
                      <View className="flex-row items-center gap-2">
                        <ExamBoardMarkerIcon
                          className="size-5"
                          pathClassName="stroke-black"
                        />
                        <Text
                          className="font-geologica-regular font-regular text-black text-sm leading-[1.5] flex-1"
                          numberOfLines={1}
                        >
                          Exam Board marker
                        </Text>
                      </View>
                    )}
                  </Pressable>
                ))}
              </View>
            )}
          </View>
        </ResponsiveContent>
      </ScrollView>
    </View>
  );
}

export default FindTutorScreen;
